import { createContext, useContext, useState, ReactNode, FC, useCallback } from 'react';
// Services
import LocalStorageService from 'app/services/LocalStorage.service';

export interface IInvoiceColumn {
  id: string;
  labelKey: string;
}

interface ContextType {
  visibleColumns: string[];
  visibleColumnsCount: number;
  toggleColumn: (id: string) => void;
  allColumns: IInvoiceColumn[];
  isVisible: (id: string) => boolean;
}

const defaultVisibleColumns = [
  'invoiceNumber',
  'billingPeriod',
  'status',
  'dueDate',
  'amount',
];

const allColumnsConfig: IInvoiceColumn[] = [
  { id: 'invoiceNumber', labelKey: 'labels.invoiceNumber' },
  { id: 'billingPeriod', labelKey: 'labels.billingPeriod' },
  { id: 'client', labelKey: 'labels.client' },
  { id: 'description', labelKey: 'labels.description' },
  { id: 'case', labelKey: 'labels.case' },
  { id: 'status', labelKey: 'labels.status' },
  { id: 'dueDate', labelKey: 'labels.dueDate' },
  { id: 'amount', labelKey: 'labels.amount' },
];

const Context = createContext<ContextType | undefined>(undefined);

export const useInvoiceColumnsContext = ():ContextType => {
  const context = useContext(Context);
  if ( !context ) throw new Error('useInvoiceColumnsContext must be used within an InvoiceColumnsProvider');
  return context;
}

type Props = {
  children: ReactNode;
}

const InvoiceColumnsProvider:FC<Props> = ({ children }) => {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(LocalStorageService.getInvoicesPreferences('visibleColumns') || defaultVisibleColumns);

  const toggleColumn = useCallback((id: string) => {
    setVisibleColumns(prev => {
      // Prevent hiding the last visible column
      if (prev.includes(id) && prev.length === 1) {
        return prev;
      }
      const next = prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id];
      LocalStorageService.setInvoicesPreferences('visibleColumns', next);
      return next;
    });
  }, []);

  const isVisible = useCallback((id: string):boolean => visibleColumns.includes(id), [visibleColumns]);

  return (
    <Context.Provider value={{ visibleColumns, toggleColumn, allColumns: allColumnsConfig, visibleColumnsCount: visibleColumns.length, isVisible }}>
      {children}
    </Context.Provider>
  );
};

export default InvoiceColumnsProvider;
